// Pure calculation functions — no React, no Firestore calls
import { computeDiscountShortages } from '../lib/discountShortages'
import { getProductPrice } from '../lib/productPricing'

// Legacy cross-pool allocations — kept for `prod.crossPoolAlloc` fallback and
// for any consumer that imports this constant directly. Reconciliation now
// prefers `getDynamicCrossPoolAlloc` so that staff-entered daily piece costs
// drive the rand split instead of the historical R30/R18 hardcode.
export const CROSS_POOL_ALLOC = {
  FS:  { steak: 60, vors: 18 },
  HST: { steak: 30, vors: 18 },
}

// Legacy fixed piece counts — same story as CROSS_POOL_ALLOC: kept for tests
// and any direct importers. The runtime path uses `getDynamicPiecesPerMeal`
// because piece counts change when vors is dropped from the day's plate.
export const STEAK_PIECES_PER_MEAL = { FS: 2, HST: 1 }
export const VORS_PIECES_PER_MEAL = { FS: 1, HST: 1 }

// ─── Dynamic daily pricing helpers ─────────────────────────────────────────
// On historical dates these defaults match the legacy hardcoded values:
//   FS  → 2.0 × 30 = R60 steak + 1 × 18 = R18 vors
//   HST → 1.0 × 30 = R30 steak + 1 × 18 = R18 vors
const FALLBACK_STEAK_UNIT_PRICE = 30
const FALLBACK_VORS_UNIT_PRICE = 18

/**
 * Read today's piece-cost cache for a kitchen day plus the steak-service
 * mode. All three keys live in localStorage and are populated by
 * saveMorningLog (prices) and setVorsWithSteak (mode selector).
 *
 * Returns the legacy hardcoded values for any (dept, date) that has never
 * had a morning log saved on this device — guarantees historical reads
 * never crash and stay numerically identical to pre-refactor behaviour.
 *
 * `steakMode` is one of:
 *   'vors'         — default, full plate is 2 steak + 1 vors
 *   'no_vors'      — vors not served, full plate is 2.5 steak only
 *   'extra_steak'  — vors not served AND plate rebalanced to 3 steak only
 *
 * Backward compatibility: legacy boolean strings ('true'/'false') from
 * before the 3-option migration are normalised to the enum.
 */
export function getDailyPrices(dept, date) {
  let steakUP = FALLBACK_STEAK_UNIT_PRICE
  let vorsUP = FALLBACK_VORS_UNIT_PRICE
  let steakMode = 'vors'
  if (dept && date && typeof localStorage !== 'undefined') {
    try {
      const s = parseFloat(localStorage.getItem(`desres_steak_unit_price_${dept}_${date}`))
      if (Number.isFinite(s) && s > 0) steakUP = s
      const v = parseFloat(localStorage.getItem(`desres_vors_unit_price_${dept}_${date}`))
      if (Number.isFinite(v) && v > 0) vorsUP = v
      const raw = localStorage.getItem(`desres_vors_with_steak_${dept}_${date}`)
      if (raw === 'vors' || raw === 'no_vors' || raw === 'extra_steak') {
        steakMode = raw
      } else if (raw === 'true') {
        steakMode = 'vors'   // legacy boolean → new enum
      } else if (raw === 'false') {
        steakMode = 'no_vors' // legacy boolean → new enum
      }
      // anything else (null/missing/garbage) → keep default 'vors'
    } catch { /* localStorage unavailable — keep fallbacks */ }
  }
  return { steakUP, vorsUP, steakMode }
}

/**
 * Build the FS/HST cross-pool allocation map from today's prices and the
 * steak-service mode. Three branches:
 *
 *   'vors'        → FS = 2 steak + 1 vors, HST = 1 steak + 1 vors
 *   'no_vors'     → FS = 2.5 steak only,   HST = 1.5 steak only
 *   'extra_steak' → FS = 3 steak only,     HST = 2 steak only
 *
 * Unrecognised modes default to 'vors' so callers cannot accidentally
 * disable cross-pool allocation by passing a typo.
 */
export function getDynamicCrossPoolAlloc(steakUP, vorsUP, steakMode) {
  if (steakMode === 'extra_steak') {
    return {
      FS:  { steak: 3.0 * steakUP, vors: 0 },
      HST: { steak: 2.0 * steakUP, vors: 0 },
    }
  }
  if (steakMode === 'no_vors') {
    return {
      FS:  { steak: 2.5 * steakUP, vors: 0 },
      HST: { steak: 1.5 * steakUP, vors: 0 },
    }
  }
  // default: 'vors'
  return {
    FS:  { steak: 2.0 * steakUP, vors: 1 * vorsUP },
    HST: { steak: 1.0 * steakUP, vors: 1 * vorsUP },
  }
}

/**
 * Per-meal physical piece counts that match the same FS/HST math used by
 * `getDynamicCrossPoolAlloc`. Steak counts shift across all 3 modes; vors
 * counts collapse to 0 in any mode where vors isn't served, so cross-pool
 * piece variance also stops counting them.
 */
export function getDynamicPiecesPerMeal(steakMode) {
  if (steakMode === 'extra_steak') {
    return {
      steak: { FS: 3.0, HST: 2.0 },
      vors:  { FS: 0,   HST: 0 },
    }
  }
  if (steakMode === 'no_vors') {
    return {
      steak: { FS: 2.5, HST: 1.5 },
      vors:  { FS: 0,   HST: 0 },
    }
  }
  // default: 'vors'
  return {
    steak: { FS: 2.0, HST: 1.0 },
    vors:  { FS: 1,   HST: 1 },
  }
}

/**
 * Determine whether a sale is a MEAL (plate/full/half portion) vs STANDALONE.
 * Meals include pap, sides etc. — only the meat component should be counted
 * against the meat pool. Standalone items use their full sale price.
 */
export function isMealSale(productName = '', productCode = '') {
  const keywords = ['plate', 'full', 'half', 'hafu']
  const combined = `${productName} ${productCode}`.toLowerCase()
  return keywords.some(k => combined.includes(k))
}

/**
 * Determine pap cost (in rands) for a given product.
 * Uses an explicit papCost field if present, otherwise infers from product name.
 */
export function getPapCostForProduct(product, papConfig) {
  if (!product) return 0
  if (product.papCost != null) return Number(product.papCost)
  const cfg = papConfig || { full: 20, half: 12, plate: 10 }
  const n = (product.name || '').toLowerCase()
  if (n.includes('full')) return cfg.full
  if (n.includes('hafu') || n.includes('half')) return cfg.half
  if (n.includes('plate')) return cfg.plate
  return 0
}

function getVarianceStatus(variancePct) {
  if (variancePct === null || variancePct === undefined) return null
  const abs = Math.abs(variancePct)
  if (abs <= 5) return 'ok'
  if (abs <= 15) return 'review'
  return 'flag'
}

/**
 * Reconcile a single category.
 *
 * @param {object} cat          - category config object (name, type, label, …)
 * @param {number} morningValue - raw value from morning log entry
 * @param {Array}  catSales     - sales records whose product belongs to this category
 * @param {Array}  catProducts  - all products that belong to this category
 * @param {Array}  allSalesForPap - all sales across all categories (used only for pap)
 * @param {Array}  allProducts  - all products (used for pap cost lookup across categories)
 * @returns {object} row
 */
export function reconcileCategory(cat, morningValue, catSales, catProducts, allSalesForPap, allProducts, papConfig, dept = null, date = null) {
  const catKey = (cat.name || '').toLowerCase()
  // Resolve today's dynamic prices for cross-pool allocation. Falls back to
  // legacy R30/R18 when dept/date are absent (e.g. unit-test callers) or
  // when no morning log has been saved for the day yet.
  const { steakUP, vorsUP, steakMode } = getDailyPrices(dept, date)
  const dynamicAlloc = getDynamicCrossPoolAlloc(steakUP, vorsUP, steakMode)
  // Extract numeric value and optional pieces from morningValue (can be number or {value, pieces})
  const mvNum = typeof morningValue === 'object' ? Number(morningValue?.value) || 0 : Number(morningValue) || 0
  const mvPieces = typeof morningValue === 'object' ? (morningValue?.pieces ? Number(morningValue.pieces) : null) : null
  const mvPiecesEstimated = typeof morningValue === 'object' ? !!morningValue?.piecesAreEstimated : false

  const productByCode = {}
  for (const p of (allProducts || catProducts)) {
    productByCode[(p.code || '').toLowerCase()] = p
  }

  // ── PAP ────────────────────────────────────────────────────────────────────
  if (catKey === 'pap only') {
    const openingPool = mvNum
    let papConsumed = 0
    let fullCount = 0
    let halfCount = 0
    let plateCount = 0

    for (const sale of (allSalesForPap || [])) {
      const prod = productByCode[(sale.productCode || '').toLowerCase()]
      if (!prod || prod.saleType === 'continuous_mirror') continue
      if ((prod.category || '').toLowerCase() === 'pap only') continue
      const papCost = getPapCostForProduct(prod, papConfig)
      const qty = Number(sale.qtySold) || 0
      if (papCost > 0) {
        papConsumed += qty * papCost
        const n = (prod.name || '').toLowerCase()
        if (n.includes('full')) fullCount += qty
        else if (n.includes('hafu') || n.includes('half')) halfCount += qty
        else if (n.includes('plate')) plateCount += qty
      }
    }

    const leftoverRand = openingPool - papConsumed
    const variancePct = openingPool > 0 ? (leftoverRand / openingPool) * 100 : null
    return {
      category: cat.name,
      label: cat.label || 'Pap Only',
      type: 'pap',
      openingValue: openingPool,
      openingUnit: 'R',
      consumed: papConsumed,
      consumedUnit: 'R',
      leftoverRand,
      variancePct,
      grossRevenue: 0,
      cogsRand: papConsumed,
      grossMargin: -papConsumed,
      status: getVarianceStatus(variancePct),
      note: 'Pap cost inferred from meal name (Full=R20, Hafu/Half=R12, Plate=R10)',
      papBreakdown: { fullCount, halfCount, plateCount },
      productDetails: [],
    }
  }

  // Branch-aware product price. Legacy docs fall back to unitPrice.
  const priceOf = (p) => {
    const px = getProductPrice(p, dept)
    return px == null ? 0 : Number(px) || 0
  }

  // ── DISCRETE ───────────────────────────────────────────────────────────────
  if (cat.type === 'discrete') {
    const openingPieces = mvNum

    // baseProduct = lowest *effective* cost per piece in this category.
    // Effective cost = unitPrice ÷ qps (e.g. KHW is R12 for 3 pieces → R4/pc, not R12).
    // For products with qps=null the unit IS one piece, so effective cost = unitPrice.
    const discreteProducts = catProducts.filter(p => p.saleType === 'discrete' && priceOf(p) > 0)
    discreteProducts.sort((a, b) => {
      const aCost = priceOf(a) / (Number(a.qps) || 1)
      const bCost = priceOf(b) / (Number(b.qps) || 1)
      return aCost - bCost
    })
    const baseProduct = discreteProducts[0] || null
    // pieceCost = how much one individual piece costs in rands
    const pieceCost = baseProduct
      ? priceOf(baseProduct) / (Number(baseProduct.qps) || 1)
      : 0
    const openingRandValue = openingPieces * pieceCost

    let piecesConsumed = 0
    let grossRevenue = 0
    const productDetails = []

    for (const sale of catSales) {
      const prod = productByCode[(sale.productCode || '').toLowerCase()]
      if (!prod || prod.saleType === 'continuous_mirror') continue
      const qty = Number(sale.qtySold) || 0
      const qps = prod.qps != null ? Number(prod.qps) : 1
      const pieces = qty * qps
      const prodPrice = priceOf(prod)
      const customerPaid = qty * prodPrice
      const meal = isMealSale(prod.name, prod.code)
      // Meat contribution: meals use piece cost × pieces; standalone uses full price
      const meatValue = meal ? pieces * pieceCost : customerPaid
      piecesConsumed += pieces
      grossRevenue += customerPaid
      productDetails.push({
        code: prod.code,
        name: prod.name,
        qtySold: qty,
        qps,
        piecesUsed: pieces,
        unitPrice: prodPrice,
        revenue: customerPaid,
        meatValue,
        isMeal: meal,
      })
    }

    const cogsRand = piecesConsumed * pieceCost
    const leftoverPieces = openingPieces - piecesConsumed
    const leftoverRand = leftoverPieces * pieceCost
    const variancePct = openingRandValue > 0 ? (leftoverRand / openingRandValue) * 100 : null
    const grossMargin = grossRevenue - cogsRand

    return {
      category: cat.name,
      label: cat.label,
      type: 'discrete',
      openingValue: openingPieces,
      openingUnit: 'pieces',
      openingRandValue,
      pieceCost,
      baseProductCode: baseProduct?.code || '—',
      consumed: piecesConsumed,
      consumedUnit: 'pieces',
      leftoverPieces,
      leftoverRand,
      variancePct,
      grossRevenue,
      cogsRand,
      grossMargin,
      status: getVarianceStatus(variancePct),
      note: baseProduct
        ? `Base piece: ${baseProduct.code} "${baseProduct.name}" = R${pieceCost}`
        : 'No base piece product found',
      productDetails,
    }
  }

  // ── CONTINUOUS (pool-based, has continuous_mirror) ─────────────────────────
  if (cat.type === 'continuous_variable') {
    const openingPool = mvNum
    const openingPieces = mvPieces
    const hasContinuousFixed = catProducts.some(p => p.saleType === 'continuous_fixed')

    let poolConsumed = 0
    let grossRevenue = 0
    let hasDiscreteSales = false
    const productDetails = []

    for (const sale of catSales) {
      const prod = productByCode[(sale.productCode || '').toLowerCase()]
      if (!prod || prod.saleType === 'continuous_mirror') continue
      const qty = Number(sale.qtySold) || 0
      const prodPrice = priceOf(prod)
      const customerPaid = qty * prodPrice     // qty × unitPrice = POS expected
      const posActual = Number(sale.salesRands) || 0 // actual rands from POS
      const meal = isMealSale(prod.name, prod.code)
      grossRevenue += customerPaid

      // Cross-pool allocation: use only this category's portion.
      // Dynamic alloc (driven by today's piece costs + vors toggle) takes
      // priority over the per-product `crossPoolAlloc` field, which itself
      // takes priority over the legacy hardcoded constant.
      const crossAlloc = dynamicAlloc[prod.code] || prod.crossPoolAlloc || CROSS_POOL_ALLOC[prod.code]
      if (crossAlloc) {
        const thisCatAlloc = crossAlloc[catKey] || 0
        const poolUsed = qty * thisCatAlloc
        poolConsumed += poolUsed
        productDetails.push({
          code: prod.code,
          name: prod.name,
          qtySold: qty,
          poolUsed,
          unitPrice: prodPrice,
          expectedAmount: customerPaid,   // qty × unitPrice
          posAmount: posActual,           // sale.salesRands — what customer paid
          revenue: customerPaid,
          meatValue: poolUsed,
          isMeal: meal,
          note: `cross-pool: R${thisCatAlloc} to ${catKey}`,
        })
      } else if (prod.saleType === 'continuous_fixed') {
        const poolUsed = qty * (Number(prod.qps) || 0)
        poolConsumed += poolUsed
        productDetails.push({
          code: prod.code,
          name: prod.name,
          qtySold: qty,
          qps: prod.qps,
          poolUsed,
          unitPrice: prodPrice,
          expectedAmount: customerPaid,
          posAmount: posActual,
          revenue: customerPaid,
          meatValue: poolUsed,
          isMeal: meal,
        })
      } else if (prod.saleType === 'discrete') {
        const poolUsed = meal ? 0 : customerPaid
        if (!meal) poolConsumed += poolUsed
        hasDiscreteSales = true
        productDetails.push({
          code: prod.code,
          name: prod.name,
          qtySold: qty,
          qps: prod.qps,
          poolUsed: poolUsed || null,
          unitPrice: prodPrice,
          expectedAmount: customerPaid,
          posAmount: posActual,
          revenue: customerPaid,
          meatValue: poolUsed,
          isMeal: meal,
          note: meal ? 'meal — meat extracted via continuous_fixed QPS' : 'standalone — full price deducted from pool',
        })
      }
    }

    const leftoverRand = openingPool - poolConsumed
    const variancePct = openingPool > 0 ? (leftoverRand / openingPool) * 100 : null
    const cogsRand = poolConsumed
    const grossMargin = grossRevenue - cogsRand

    // Piece variance check (optional — only when openingPieces provided)
    let pieceVariance = null
    if (openingPieces != null && openingPieces > 0 && openingPool > 0) {
      const pieceCost = openingPool / openingPieces
      let piecesInMeals = 0
      let standalonePieces = 0
      // Pick the correct fixed piece allocation table for this category.
      // Uses the dynamic 3-mode helper so 'extra_steak' / 'no_vors' days
      // count steak pieces per meal correctly (FS=3 / HST=2 in extra_steak,
      // FS=2.5 / HST=1.5 in no_vors). Without this, piece variance for
      // steak undercounts FS sales by 1 piece in extra_steak mode.
      const dynamicPieces = getDynamicPiecesPerMeal(steakMode)
      const pieceTable = catKey === 'steak' ? dynamicPieces.steak
        : catKey === 'vors' ? dynamicPieces.vors
        : null
      for (const d of productDetails) {
        if (pieceTable && pieceTable[d.code]) {
          // Fixed piece count per meal (not derived from rand ratio)
          piecesInMeals += d.qtySold * pieceTable[d.code]
        } else if (d.isMeal && d.qps) {
          piecesInMeals += d.qtySold * (Number(d.qps) || 0)
        } else if (!d.isMeal && d.poolUsed) {
          standalonePieces += pieceCost > 0 ? d.poolUsed / pieceCost : 0
        }
      }
      // Surplus pieces: use staff-entered surplus if available, else derive from leftover
      const enteredSurplusRands = typeof morningValue === 'object' ? (morningValue?.surplusRands ?? null) : null
      const surplusForPieces = enteredSurplusRands != null ? enteredSurplusRands : (leftoverRand > 0 ? leftoverRand : 0)
      const surplusPieces = surplusForPieces > 0 && pieceCost > 0 ? surplusForPieces / pieceCost : 0
      const totalPiecesUsed = piecesInMeals + standalonePieces
      const piecesAvailable = openingPieces - surplusPieces
      pieceVariance = {
        openingPieces,
        pieceCost: Math.round(pieceCost * 100) / 100,
        piecesInMeals: Math.round(piecesInMeals * 100) / 100,
        standalonePieces: Math.round(standalonePieces * 100) / 100,
        surplusPieces: Math.round(surplusPieces * 100) / 100,
        totalUsed: Math.round(totalPiecesUsed * 100) / 100,
        variance: Math.round((piecesAvailable - totalPiecesUsed) * 100) / 100,
        piecesAreEstimated: mvPiecesEstimated,
      }
    }

    return {
      category: cat.name,
      label: cat.label,
      type: 'continuous',
      openingValue: openingPool,
      openingUnit: 'R',
      consumed: poolConsumed,
      consumedUnit: 'R',
      leftoverRand,
      variancePct,
      grossRevenue,
      cogsRand,
      grossMargin,
      status: getVarianceStatus(variancePct),
      pieceVariance,
      note: hasDiscreteSales
        ? 'Some discrete sales exist in this category — not deducted from pool'
        : hasContinuousFixed
          ? null
          : 'No continuous_fixed products — pool deduction is 0',
      productDetails,
    }
  }

  // ── FIXED UNIT ─────────────────────────────────────────────────────────────
  if (cat.type === 'fixed_unit') {
    let grossRevenue = 0
    const productDetails = []

    for (const sale of catSales) {
      const prod = productByCode[(sale.productCode || '').toLowerCase()]
      if (!prod) continue
      const qty = Number(sale.qtySold) || 0
      const prodPrice = priceOf(prod)
      const rev = qty * prodPrice
      grossRevenue += rev
      productDetails.push({
        code: prod.code,
        name: prod.name,
        qtySold: qty,
        unitPrice: prodPrice,
        revenue: rev,
      })
    }

    return {
      category: cat.name,
      label: cat.label,
      type: 'fixed_unit',
      openingValue: null,
      openingUnit: null,
      consumed: null,
      consumedUnit: null,
      leftoverRand: null,
      variancePct: null,
      grossRevenue,
      cogsRand: 0,
      grossMargin: grossRevenue,
      status: null,
      note: 'Fixed unit — no opening pool tracked',
      productDetails,
    }
  }

  return null
}

/**
 * Build a full reconciliation from morning logs, sales, products, and categories.
 *
 * @param {Array} morningLogs  - array of morning log entries (from Firestore morningLogs/{date}.entries)
 * @param {Array} sales        - array of sale transactions from getSalesTransactions()
 * @param {Array} products     - all products for the department
 * @param {Array} categories   - all category configs
 * @returns {{ rows: Array, pap: object|null, totals: object }}
 */
export async function buildFullReconciliation(morningLogs, sales, products, categories, papConfig, opts = {}) {
  // opts.getLastPieceCost: optional function(dept, category, date) → number|null
  // opts.dept: department ID for piece cost lookups
  // opts.date: current date for piece cost lookups
  const { getLastPieceCost: getPieceCost, dept, date } = opts

  // Build morning map: category → { value, pieces, pieceCost, piecesAreEstimated }
  const PIECE_COST_CATEGORIES = ['steak', 'vors', 'stew vors', 'magwanda']
  const morningMap = {}
  for (const entry of (morningLogs || [])) {
    const key = (entry.categoryCode || entry.category || '').toLowerCase()
    const value = Number(entry.rawValue ?? entry.value ?? 0)
    const pieces = entry.openingPieces ? Number(entry.openingPieces) : null
    let pieceCost = entry.pieceCost ? Number(entry.pieceCost) : null
    let piecesAreEstimated = false

    if (pieces && pieces > 0 && value > 0) {
      // Staff entered pieces today — use them directly
      pieceCost = Math.round((value / pieces) * 100) / 100
    } else if (!pieces && value > 0 && PIECE_COST_CATEGORIES.includes(key) && getPieceCost && dept && date) {
      // No pieces entered — try to derive from last known piece cost
      const lastCost = await getPieceCost(dept, key, date)
      if (lastCost && lastCost > 0) {
        const impliedPieces = Math.round(value / lastCost)
        morningMap[key] = { value, pieces: impliedPieces, pieceCost: lastCost, piecesAreEstimated: true }
        continue
      }
    }

    morningMap[key] = { value, pieces, pieceCost, piecesAreEstimated }
  }

  const productByCode = {}
  for (const p of (products || [])) {
    productByCode[(p.code || '').toLowerCase()] = p
  }

  const rows = []
  let papRow = null

  for (const cat of (categories || [])) {
    const catKey = (cat.name || '').toLowerCase()
    const morningRaw = morningMap[catKey] || { value: 0, pieces: null }
    // Pass the full object to reconcileCategory so piece variance can access openingPieces
    const morningValue = typeof morningRaw === 'object' ? morningRaw : { value: Number(morningRaw) || 0, pieces: null }
    const catProducts = (products || []).filter(p => (p.category || '').toLowerCase() === catKey)
    const catSales = (sales || []).filter(s => {
      // Primary: look up product and check its category
      const prod = productByCode[(s.productCode || '').toLowerCase()]
      if (prod) return (prod.category || '').toLowerCase() === catKey
      // Fallback: use the category field stored directly on the sale record
      return (s.category || '').toLowerCase() === catKey
    })

    const row = reconcileCategory(cat, morningValue, catSales, catProducts, sales, products, papConfig, dept, date)
    if (!row) continue

    if (catKey === 'pap only') {
      papRow = row
    } else {
      rows.push(row)
    }
  }

  // Resolve dynamic pricing once for the second pass — same source of truth
  // as reconcileCategory uses internally.
  const { steakUP: _steakUP, vorsUP: _vorsUP, steakMode } = getDailyPrices(dept, date)
  const dynamicAllocFull = getDynamicCrossPoolAlloc(_steakUP, _vorsUP, steakMode)
  const dynamicPieces = getDynamicPiecesPerMeal(steakMode)

  // Second pass: apply cross-pool contributions (rands + pieces)
  const crossPoolAccum = {}
  const crossPoolPieces = {} // { categoryName: additionalPiecesFromMeals }
  // Audit trail: per-destination-pool list of source records so the UI and
  // PDF can show *which* sales pulled stock from each pool. Mirrors the
  // shape used by DailyOps.jsx (`crossPoolDetails` on the row).
  const crossPoolDetails = {}
  for (const row of rows) {
    for (const d of (row.productDetails || [])) {
      const prod = productByCode[(d.code || '').toLowerCase()]
      // Same priority order as reconcileCategory: dynamic → per-product → legacy.
      const crossAllocFull = dynamicAllocFull[prod?.code] || prod?.crossPoolAlloc || CROSS_POOL_ALLOC[prod?.code]
      if (!crossAllocFull) continue
      for (const [poolName, allocPerUnit] of Object.entries(crossAllocFull)) {
        const poolKey = poolName.toLowerCase()
        if (poolKey === row.category.toLowerCase()) continue
        if (!crossPoolAccum[poolKey]) crossPoolAccum[poolKey] = 0
        crossPoolAccum[poolKey] += d.qtySold * allocPerUnit
        // Record the source sale so the destination pool can show its
        // own audit trail. Skip zero-allocation entries (e.g. vors when
        // steakMode is 'no_vors' or 'extra_steak' sets vors:0) so they
        // don't pollute the UI.
        if (allocPerUnit > 0) {
          if (!crossPoolDetails[poolKey]) crossPoolDetails[poolKey] = []
          crossPoolDetails[poolKey].push({
            code: d.code,
            name: prod?.name || d.code,
            qtySold: d.qtySold,
            allocPerUnit,
            total: d.qtySold * allocPerUnit,
            sourceCategory: row.category,
          })
        }
        // Accumulate piece contributions for piece variance using the
        // dynamic per-meal counts (steak counts shift when vors is dropped).
        const pieceLookup = poolKey === 'vors' ? dynamicPieces.vors
          : poolKey === 'steak' ? dynamicPieces.steak : null
        if (pieceLookup && pieceLookup[d.code]) {
          if (!crossPoolPieces[poolKey]) crossPoolPieces[poolKey] = 0
          crossPoolPieces[poolKey] += d.qtySold * pieceLookup[d.code]
        }
      }
    }
  }
  for (const [poolName, extraConsumed] of Object.entries(crossPoolAccum)) {
    const row = rows.find(r => r.category.toLowerCase() === poolName)
    if (row && row.type === 'continuous') {
      row.consumed += extraConsumed
      row.cogsRand += extraConsumed
      row.leftoverRand -= extraConsumed
      row.variancePct = row.openingValue > 0 ? (row.leftoverRand / row.openingValue) * 100 : null
      row.grossMargin = row.grossRevenue - row.cogsRand
      row.status = getVarianceStatus(row.variancePct)
      // Audit trail (no math impact): expose where this pool's cross-pool
      // contribution came from so the UI and PDF can render a breakdown.
      row.crossPoolConsumed = extraConsumed
      row.crossPoolDetails = crossPoolDetails[poolName] || []
      // Add cross-pool pieces to piece variance if it exists
      const extraPieces = crossPoolPieces[poolName.toLowerCase()] || 0
      if (row.pieceVariance && extraPieces > 0) {
        row.pieceVariance.piecesInMeals = Math.round((row.pieceVariance.piecesInMeals + extraPieces) * 100) / 100
        row.pieceVariance.totalUsed = Math.round((row.pieceVariance.totalUsed + extraPieces) * 100) / 100
        row.pieceVariance.variance = Math.round((row.pieceVariance.variance - extraPieces) * 100) / 100
      }
    }
  }

  // Vors suppression: when the kitchen is not serving vors today (either
  // 'no_vors' or 'extra_steak' mode), the vors row should never produce a
  // status, flag, or piece variance — there is no pool to reconcile
  // against. Cross-pool contributions from FS/HST are already R0 because
  // dynamicAllocFull has vors:0 for both products in both modes.
  if (steakMode !== 'vors') {
    const vorsRow = rows.find(r => (r.category || '').toLowerCase() === 'vors')
    if (vorsRow) {
      vorsRow.status = null
      vorsRow.flag = null
      vorsRow.displayNote = 'No vors today'
      if (vorsRow.pieceVariance) vorsRow.pieceVariance = null
    }
  }

  const totals = {
    totalOpeningRand: rows
      .filter(r => r.openingUnit === 'R')
      .reduce((s, r) => s + (r.openingValue || 0), 0),
    totalConsumedRand: rows.reduce((s, r) => s + (r.cogsRand || 0), 0),
    totalRevenue: rows.reduce((s, r) => s + (r.grossRevenue || 0), 0),
    totalMargin: rows.reduce((s, r) => s + (r.grossMargin || 0), 0),
    flagCount: rows.filter(r => r.status === 'flag').length,
    reviewCount: rows.filter(r => r.status === 'review').length,
    okCount: rows.filter(r => r.status === 'ok').length,
  }

  // Discount shortages: items sold below their standard unitPrice. Pure
  // helper, computed independently of the reconciliation rows so it
  // catches discount events even on categories that otherwise reconcile
  // cleanly. Free items (actual = 0, expected > 0) are returned in a
  // separate list so dashboards can distinguish "discounted" from
  // "given away free".
  const discountShortages = computeDiscountShortages(sales, products, dept)

  return { rows, pap: papRow, totals, discountShortages }
}
